import React from "react";
import { Icon } from "@iconify/react";
import Button from "../Button";
import { Typography } from "../Typography";
import {
  IDEA_FLOW_STAGE_NAMES,
  formatIdeaStatusLabel,
} from "../../Container/IdeasReview/utils";

export const filterEngagementIdeas = (ideas, { searchTerm = "", statusFilter = "", stageFilter = "" } = {}) => {
  let filtered = ideas;

  if (statusFilter) {
    filtered = filtered.filter(
      (idea) => (idea.status || "").toLowerCase() === statusFilter.toLowerCase()
    );
  }

  if (stageFilter) {
    filtered = filtered.filter(
      (idea) => (idea.stepName || "").toLowerCase() === stageFilter.toLowerCase()
    );
  }

  if (searchTerm.trim()) {
    const q = searchTerm.toLowerCase();
    filtered = filtered.filter((idea) => {
      const title = (idea.title || "").toLowerCase();
      const category = (idea.category || "").toLowerCase();
      const status = (idea.status || "").toLowerCase();
      const step = (idea.stepName || "").toLowerCase();
      const impact = (idea.primaryImpact || "").toLowerCase();
      const student = (idea.tags || []).map((t) => t.label).join(" ").toLowerCase();
      return (
        title.includes(q) ||
        category.includes(q) ||
        status.includes(q) ||
        step.includes(q) ||
        impact.includes(q) ||
        student.includes(q) ||
        String(idea.id).includes(q)
      );
    });
  }

  return filtered;
};

const EngagementsFilterBar = ({
  ideas = [],
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  stageFilter,
  onStageChange,
  showFilters,
  onToggleFilters,
  onClearFilters,
}) => {
  const statusOptions = Array.from(
    new Set(ideas.map((i) => i.status).filter(Boolean))
  ).sort();

  const hasActiveFilters = Boolean(searchTerm.trim() || statusFilter || stageFilter);

  return (
    <div className="flex flex-col gap-3 w-full sm:w-auto sm:min-w-[280px]">
      <Button
        variant="secondary"
        size="medium"
        type="button"
        startIcon={<Icon icon="lucide:filter" className="h-5 w-5" />}
        onClick={onToggleFilters}
        className={showFilters ? "ring-2 ring-violet-200 border-violet-300" : ""}
      >
        Filter
        {hasActiveFilters && (
          <span className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-violet-600 px-1.5 text-[11px] font-bold text-white">
            {[searchTerm.trim(), statusFilter, stageFilter].filter(Boolean).length}
          </span>
        )}
      </Button>

      {showFilters && (
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-lg sm:absolute sm:right-0 sm:top-full sm:z-20 sm:mt-2 sm:min-w-[320px]">
          <div className="flex items-center justify-between">
            <Typography as="p" className="text-sm font-semibold text-slate-800">
              Filter engagements
            </Typography>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="text-xs font-medium text-violet-600 hover:text-violet-800"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search title, student, stage..."
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
            <Icon
              icon="lucide:search"
              className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
          </div>

          {statusOptions.length > 0 && (
            <select
              value={statusFilter}
              onChange={(e) => onStatusChange(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:border-violet-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatIdeaStatusLabel(status)}
                </option>
              ))}
            </select>
          )}

          <select
            value={stageFilter}
            onChange={(e) => onStageChange(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:border-violet-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-200"
          >
            <option value="">All stages</option>
            {IDEA_FLOW_STAGE_NAMES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default EngagementsFilterBar;
